/* eslint-disable no-console */
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import { withStyles } from '@material-ui/core/styles'
import { Issuer } from 'openid-client'
import { useFirestoreConnect } from 'react-redux-firebase'
import { withRouter } from 'react-router'
import { compose } from 'redux'
import Type from '../../Components/Type'
import L from '../../Components/L'
import iconBuffering from '../../Images/icon_buffering.svg'
import {
  USE_LOCAL,
  OIDC_CONFIG_LOCAL,
  OIDC_CLIENT_ID,
  ENVIRONMENT,
  OIDC_CONFIG,
  WEBSITE_CONFIG_ID,
  WEBSITE_CONFIG_COLLECTION,
} from '../../Config'
import LoginSSOStyles from './LoginSSO-styles'

const enhance = compose(withRouter, withStyles(LoginSSOStyles))

const LoginSSOScreen = ({ match: { url }, classes }) => {
  useFirestoreConnect([
    {
      collection: WEBSITE_CONFIG_COLLECTION,
      where: ['id', '==', WEBSITE_CONFIG_ID],
    },
  ])

  const data = useSelector(({ firestore }) => firestore.data)
  const config = data?.[WEBSITE_CONFIG_COLLECTION]?.[WEBSITE_CONFIG_ID] || {}
  const { sso } = config

  const getClient = async () => {
    let oidcIssuer // => Issuer
    // Force Local OIDC
    if (USE_LOCAL) {
      oidcIssuer = new Issuer(OIDC_CONFIG_LOCAL)
      return new oidcIssuer.Client({
        client_id: OIDC_CLIENT_ID,
      }) // => Client
    }
    console.log('getClient', sso, ENVIRONMENT)
    if (sso?.environments?.[ENVIRONMENT]?.issuer) {
      // OIDC Issuer has been overridden
      console.log(`enviroment issuer found: ${sso.environments[ENVIRONMENT].issuer}`)
      if (
        sso.environments[ENVIRONMENT].authorization_endpoint &&
        sso.environments[ENVIRONMENT].token_endpoint &&
        sso.environments[ENVIRONMENT].userinfo_endpoint &&
        sso.environments[ENVIRONMENT].jwks_uri
      ) {
        // We do not need to run discover
        oidcIssuer = new Issuer(sso.environments[ENVIRONMENT])
      } else {
        // Run Discover
        try {
          console.log('Running OIDC Discover on ', sso.environments[ENVIRONMENT].issuer)
          oidcIssuer = await Issuer.discover(sso.environments[ENVIRONMENT].issuer)
          console.log('Finished OIDC Discover', oidcIssuer)
        } catch (e) {
          // eslint-disable-next-line no-alert
          alert(`could not reach issuer ${sso.environments[ENVIRONMENT].issuer}, check sso.json`)
          console.log(`could not reach issuer ${sso.environments[ENVIRONMENT].issuer}, check sso.json`)
          return null
        }
      }
    } else {
      oidcIssuer = new Issuer(OIDC_CONFIG)
    }
    console.log('LOG getClient creating client')
    const client = new oidcIssuer.Client({
      client_id: OIDC_CLIENT_ID,
    }) // => Client
    return client
  }

  const getLoginRedirect = async (scope = '', state = '') => {
    const redirect_uri = `${window.location.protocol}//${window.location.host}${process.env.PUBLIC_URL}/callback`
    const client = await getClient()
    return client.authorizationUrl({
      redirect_uri,
      grant_type: 'implicit', // implicit refresh_token // authorization_code
      response_type: 'id_token token',
      state,
      nonce: 'tghw4ghw435eghw345gw435gwg5wg3t4rg',
      prompt: 'login',
      scope: `openid profile email offline_access ${WEBSITE_CONFIG_ID} ${scope}`,
    })
  }

  const handleLoginRedirect = async (scope = '') => {
    // Encode current URL to use as state
    const state = encodeURI(url)
    const redirectUrl = await getLoginRedirect(scope, state)
    window.location = redirectUrl
  }

  const handleGetStarted = async () => {
    // Encode current URL to use as state
    const scope = 'forgedx'
    const redirectUrl = await getLoginRedirect(scope, 'signup')
    window.location = redirectUrl
  }

  const renderSSOButtons = () => {
    return config.sso.scopes.map((scope, i) => (
      <Grid key={`sso_login_button_${scope.id}`}>
        <Button
          color={i === 0 ? 'primary' : 'secondary'}
          variant={i === 0 ? 'contained' : 'outlined'}
          className={[classes.button, 'small-font'].join(' ')}
          onClick={() => handleLoginRedirect(scope.id)}
        >
          {scope.btnText}
        </Button>
      </Grid>
    ))
  }

  useEffect(() => {
    // If we only have 1 scope, then redirect.
    if (config.sso.scopes.length === 1) {
      handleLoginRedirect(config.sso.scopes[0].id)
    }
  }, [])

  const hero = {
    background: `url(${config.sso.leftBgImage.signedUrl}) no-repeat center / cover`,
    minHeight: '100vh',
  }

  const leftIcon = config?.sso?.leftIcon?.signedUrl || require('../../Images/LogoIdeaCloud.svg')

  // Detect Internet Explorer: https://stackoverflow.com/questions/24861073/detect-if-any-kind-of-ie-msie/24861307
  const isIE = /Trident|MSIE/.test(navigator.userAgent)

  if (isIE) {
    return (
      <div>
        <Type type="title" align="center" className={{ margin: 50 }}>
          I'm sorry, Internet Explorer is not supported.
          <br />
          Please use the Chrome browser.
          <br />
          <br />
          Download Chrome here:
          <br />
          <a href="https://www.google.com/chrome/browser/">https://www.google.com/chrome/browser/</a>
        </Type>
      </div>
    )
  }

  if (config.sso.scopes.length === 1) {
    // We are redirecting to login.
    return (
      <Grid container>
        <Grid container item style={hero} xs={3} direction="row" alignItems="center" justify="center">
          <img src={leftIcon} alt="" className={classes.leftIcon} />
        </Grid>
        <Grid
          container
          direction="column"
          item
          xs={9}
          justify="center"
          alignItems="center"
          style={{ padding: '20px 20px 15px' }}
        >
          <Grid container justify="center" style={{ margin: '0 auto', flexGrow: 1 }}>
            <L display="grid" ji="center" ac="center">
              <img
                src={iconBuffering}
                alt="Loading Spinner"
                className={classes.buffering}
                style={{ marginBottom: '10px' }}
              />
              <Type variant="headerNormal" mXAuto color="secondaryHueDark" mB={0.5}>
                Loading your
              </Type>
              <Type
                variant="title"
                align="center"
                style={{
                  fontSize: '38px',
                  lineHeight: 1,
                  color: '#142834',
                  fontWeight: 300,
                }}
              >
                <span style={{ fontWeight: 600 }}>Idea</span>Cloud
              </Type>
            </L>
          </Grid>
          <Grid container direction="column" alignItems="center">
            <img
              src={require('../../Images/LogoIdeaCloudColor.svg')}
              alt="IdeaCloud Logo"
              style={{ marginBottom: '10px', height: '30px' }}
            />
            <Type variant="descriptionAtomic">
              Powered by IdeaCloud™ {new Date().getFullYear()}. All rights reserved.
            </Type>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container>
      <Grid container item style={hero} xs={3} direction="row" alignItems="center" justify="center">
        <img src={leftIcon} alt="" className={classes.leftIcon} />
      </Grid>
      <Grid
        container
        direction="column"
        item
        xs={9}
        justify="center"
        alignItems="center"
        style={{ padding: '20px 20px 15px' }}
      >
        <Grid container alignItems="center" justify="flex-end" style={{ width: '100%' }}>
          <Grid item className={classes.noAccount}>
            <Type variant="body3">Don't have an account?</Type>
          </Grid>
          <Grid item className={classes.noAccount}>
            <Button className="small-font" variant="outlined" onClick={handleGetStarted}>
              Get Started
            </Button>
          </Grid>
        </Grid>
        <Grid container justify="center" alignItems="center" style={{ flexGrow: 1 }}>
          <Grid
            style={{
              margin: '0 auto',
              flexGrow: 1,
              display: 'grid',
              gridAutoFlow: 'row',
              gridGap: 30,
            }}
          >
            <Type
              variant="title"
              style={{
                fontSize: '24px',
                lineHeight: '33px',
                color: '#142834',
                fontWeight: 300,
                textAlign: 'center',
              }}
            >
              {config.sso.forgeLoginFormHeader}
            </Type>
            <Grid container alignItems="center" className={classes.buttonContainer}>
              {renderSSOButtons()}
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction="column" alignItems="center">
          <img
            src={require('../../Images/LogoIdeaCloudColor.svg')}
            alt="IdeaCloud Logo"
            style={{ height: '30px', marginBottom: '10px' }}
          />
          <Type variant="descriptionAtomic">
            Powered by IdeaCloud™ {new Date().getFullYear()}. All rights reserved.
          </Type>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default enhance(LoginSSOScreen)
